import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Stack, Box, IconButton, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAuth } from '../stores/authContext';
import { useBrandConfig } from '../stores/brandConfigContext';
import StepEmail from '../steps/StepEmail';

import LoginFooter from '../layouts/LoginFooter';
import ThemeToggleButton from '../components/ThemeToggleButton';
import LanguageSelector from '../components/LanguageSelector';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { state } = useAuth();
  const isPreview = window.location.hostname.includes('lovable.app') || window.location.hostname.includes('localhost');
  const { brandKeyword } = useParams();
  const { config: brandConfig } = useBrandConfig();

  const config = (window as any).PAGOS_CONFIG;
  const esPagosConMarca = config?.esPagosConMarca;

  // Redirigir si ya está autenticado (solo si NO es preview)
  useEffect(() => {
    if (!isPreview && !state.isLoading && state.isAuthenticated) {
      const homeRoute = brandKeyword || 'acme';
      navigate(`/${homeRoute}/home`, { replace: true });
    }
  }, [isPreview, state.isLoading, state.isAuthenticated, navigate, brandKeyword]);

  const handleNext = (data: { email?: string; phone?: string }) => {
    const otpRoute = `/${brandKeyword || 'acme'}/otp`;
    if (data.email) {
      navigate(otpRoute, { state: { email: data.email } });
    } else if (data.phone) {
      navigate(otpRoute, { state: { phone: data.phone } });
    }
  };

  const pageTitle = brandConfig?.nombre 
    ? `Portal de Pagos ${brandConfig.nombre}`
    : 'Portal de Pagos';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta 
          name="description" 
          content={`Accede al portal de pagos${brandConfig?.nombre ? ` de ${brandConfig.nombre}` : ''}. Consulta y paga tus facturas de forma segura.`} 
        />
        <meta property="og:title" content={pageTitle} />
        <meta 
          property="og:description" 
          content={`Portal de pagos seguro${brandConfig?.nombre ? ` de ${brandConfig.nombre}` : ''}`} 
        />
      </Helmet>
      
      <Stack
        sx={{
          minHeight: '100vh',
          backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{
            px: { xs: 2, md: 2 },
            py: 1,
            height: '80px',
          }}
        >
          {/* Controles de tema e idioma */}
          <Stack direction="row" spacing={1}>
            <ThemeToggleButton />
            <LanguageSelector />
          </Stack>
        </Stack>

        {/* Contenido principal */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            px: { xs: 2, md: 3 },
            pt: { xs: 1, md: 2 },
            pb: 2,
          }}
        >
          <StepEmail onNext={handleNext} />
        </Box>

        {/* Footer */}
        <LoginFooter />
      </Stack>
    </>
  );
};

export default LoginPage;
